import { useStore } from "@priolo/jon";
import PlaceholderCmp, { ICON_TYPE } from "components/controls/PlaceholderCmp";
import Graph from "components/planner/Graph";
import { Activity, EventMouseGantt, GraphGroup, GraphStyle, TAGS } from "components/planner/types";
import { filterActivitiesByTags, getActivityFromGroups } from 'components/planner/utils';
import TooltipCycle from "pages/farms/planner/TooltipCycle";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import cycleSo from "stores/cycle";
import farmSo from "stores/farm";
import plannerSo, { PlannerState } from "stores/planner";
import { buildTasksGroup } from "stores/planner/groups/tasksGroup";
import { buildYeldGroup } from "stores/planner/groups/yieldGroup";
import { getRangeFromEvent } from "stores/planner/utils";
import { updateActivitySelect } from "stores/planner/utils/update";
import solutionLabSo from "stores/productions/lab";
import { cyclesFromSolutionCycles } from "stores/productions/utils/solutionCycle";
import { tasksFromSolutionTasks } from "stores/productions/utils/solutionTask";
import querySo from "stores/route/query";
import { URL_PAR } from "stores/route/utils/types";
import taskSo from "stores/task";
import { Cycle } from "types/Cycle";
import { DateNumber, DateRange } from "types/global";
import { GrowUnit, GrowUnitLayer } from "types/GrowUnit";
import { GROW_UNIT_MODE, GROW_UNIT_STATUS } from "types/GrowUnitEnum";
import { SolutionCyclePhase } from "types/Production";
import { Task } from "types/Task";
import { dateToUrl, expandDateRange, includeDateInDateRange, urlToDateRange } from "utils";
import { hashStringToColor } from "utils/color";
import { dateWithoutTime, DAY_MILLISEC, diffDay } from "utils/time";



interface Props {
	style?: Partial<GraphStyle>
}

const PlannerLab: FunctionComponent<Props> = ({
	style
}) => {

	// STORES
	const plannerSa = useStore<PlannerState>(plannerSo)
	useStore(querySo)
	useStore(taskSo)
	useStore(cycleSo)


	// HOOK
	const [tipPos, setTipPos] = useState({ x: -1, y: -1 })
	const [tipActivity, setTipActivity] = useState<Activity>(null)

	// ricavo i dati URL
	const [dateSelUrl, cycleSelUrl, growUnitUrl, cropId, colorsUrl, structUrl] = useMemo(
		() => querySo.getSearchUrl([
			URL_PAR.DATE_SEL, URL_PAR.CYCLE_SEL, URL_PAR.GROWUNIT_SEL,
			URL_PAR.CROP_SEL, URL_PAR.PLANNER_COLORS, URL_PAR.PLANNER_STRUCT
		]),
		[querySo.state.queryUrl]
	)
	const dateRangeSel = urlToDateRange(dateSelUrl)

	// su selezione CYCLE e DATESEL
	useEffect(() => {
		updateActivitySelect(null, cycleSelUrl)
	}, [cycleSelUrl])


	useEffect(() => {
		if (!solutionLabSo.state.selected) return

		let dateRange: DateRange = {}

		// raggruppo per PHASE-NAME i LAYERS-ID e i SOLUTION-CYCLES
		const phasesGroups = solutionLabSo.state.selected.solutionCycles.reduce((acc, solCycle) => {
			solCycle.phases.forEach(cyclePhase => {
				const phaseGroup = acc[cyclePhase.name]
				cyclePhase.uuid = solCycle.uuid
				if (!phaseGroup) {
					acc[cyclePhase.name] = {
						layersIds: [...cyclePhase.layers],
						solutionCycles: [cyclePhase],
					}
				} else {
					phaseGroup.solutionCycles.push(cyclePhase)
					phaseGroup.layersIds = [...new Set([...phaseGroup.layersIds, ...cyclePhase.layers])]
				}
			})
			return acc
		}, {} as { [phase: string]: { solutionCycles: SolutionCyclePhase[], layersIds: number[] } })


		// ACTIVITIES CYCLES GROUP
		const rootGroup: GraphGroup = {
			name: "Phases",
			title: "PHASES",
			collapsed: false,
			groups: []
		}

		// ACTIVITIES CYCLES
		Object.entries(phasesGroups).forEach(([phaseName, cyclePhaseGroup]) => {

			const phaseLayersIds = [...cyclePhaseGroup.layersIds].sort((a, b) => a - b)

			const activities = cyclePhaseGroup.solutionCycles.flatMap(cycle => {

				const startTime = dateWithoutTime(cycle.busyFrom)
				const endTime = dateWithoutTime(cycle.busyTo)
				const delta = diffDay(endTime, startTime) + 1

				includeDateInDateRange(dateRange, startTime)
				includeDateInDateRange(dateRange, endTime)

				return cycle.layers.map(layerId => {
					const row = phaseLayersIds.indexOf(layerId)
					if (row === -1) return null

					const activity: Activity = {
						start: startTime,
						length: delta,
						row,
						color: hashStringToColor(cycle.uuid),
						tags: {
							[TAGS.CYCLES]: [cycle.uuid],
							[TAGS.GROWUNIT_PHASE]: phaseName,
							[TAGS.LAYER_NUMBER]: layerId,
						},
					}
					return activity
				})
			})

			const phaseGroup: GraphGroup = {
				name: phaseName,
				title: phaseName.toUpperCase(),
				labels: phaseLayersIds.map(l => `Layer ${l}`),
				//groups: [],
				activities,
			}

			rootGroup.groups.push(phaseGroup)
		})



		let layersCounter = 0
		// creo i layer della GROWUNIT-TEMP
		const growUnitLayers: GrowUnitLayer[] = Object.entries(phasesGroups).reduce((acc, [phaseName, phaseGroup]) => {
			const layersIdsSorted = [...phaseGroup.layersIds].sort((a, b) => a - b)
			const phaseLayers = layersIdsSorted.map(layerId => {
				layersCounter++
				return {
					id: layerId,
					growUnitId: 0,
					phase: phaseName,
					column: 1,
					row: layersCounter,
					layerName: `Layer ${layerId}`,
					layerNumber: layersCounter,
					disabled: false,
					virtual: false,
				} as GrowUnitLayer
			})
			acc.push(...phaseLayers)
			return acc
		}, [] as GrowUnitLayer[])

		// GROWUNIT-TEMP
		const growUnit: GrowUnit = {
			id: 0,
			farmId: 0,
			name: "Preview Grow Unit",
			growUnitCode: "PREVIEW-GROW-UNIT",
			mode: GROW_UNIT_MODE.HUMAN,
			status: GROW_UNIT_STATUS.RUNNING,
			typology: "X",
			isWaitingForEdge: false,
			isOffline: false,
			layers: growUnitLayers,
			desiredLayersState: null,
			currentDeployDay: 0,
			currentLayersMatrix: null,
			desiredLayersMatrix: null,
			layersMatrixFromCycles: false,
			recipe: null,
			desiredRecipe: null,
			baseRecipeId: 0,
			columnsNumber: 1,
			mqPerLayer: 0,
			layersPerColumn: growUnitLayers.length,
			connectedLayers: 1,
			totalLayersPerColumn: growUnitLayers.length,
			totalLayers: growUnitLayers.length,
			trolleysPerLayer: 0,
			mqPerTrolley: 0,
			barsPerTrolley: 0,
			totalTrolleys: 0,
			traysPerTrolley: 0,
			totalTrays: 0,
			barsPerLayer: 0,
			barsTypology: "",
			totalBars: 0,
		}
		farmSo.setSelect({ ...farmSo.state.select, growUnits: [growUnit] })

		const cycles: Cycle[] = cyclesFromSolutionCycles(
			solutionLabSo.state.selected.solutionCycles,
			[growUnit]
		)
		cycleSo.setAll(cycles)










		// TASKS
		const tasks: Task[] = tasksFromSolutionTasks(solutionLabSo.state.selected.solutionTaskGroups, 0)
		const { tasks: taskGroup } = buildTasksGroup(tasks, true)
		const yieldGroup = buildYeldGroup(cycles, true)

		const dataGroup: GraphGroup = {
			name: "Data",
			title: "DATA",
			collapsed: false,
			groups: [taskGroup, yieldGroup]
		}
		taskSo.setAll(tasks)



		plannerSo.setGroups([rootGroup, dataGroup])
		dateRange = expandDateRange(dateRange, 10)
		plannerSo.setVisibleDateRange(dateRange)
		plannerSo.setDateCurrent(dateRange.start)

	}, [solutionLabSo.state.selected])



	// HANDLE
	const handleChangeGroups = (newGroups: GraphGroup[]) => {
		plannerSo.setGroups(newGroups)
	}
	const handleHorizontalScroll = (days: number) => {
		const time = days * DAY_MILLISEC
		plannerSo.setDateCurrent(plannerSo.state.dateStart + time)
	}
	const handleActivityHighlightChanged = (e: MouseEvent, activity: Activity) => {
		const isDataItem = !!activity?.value
		let actsHighlight = null
		const cyclesUuid: string[] = activity?.tags?.[TAGS.CYCLES]

		// setto i dati per il TOOLTIP
		if (!isDataItem && activity) {
			setTipActivity(activity)
			setTipPos({ x: e.clientX, y: e.clientY })
		} else {
			setTipActivity(null)
			setTipPos({ x: -1, y: -1 })
		}

		// setto tutte le ACTIVITY che devono essere "evidenziate" (perche' hanno lo stesso CYCLE)
		if (cyclesUuid) {
			const activities = getActivityFromGroups(plannerSo.state.groups)
			actsHighlight = filterActivitiesByTags(activities, { cycles: cyclesUuid })
		}
		plannerSo.setHighlight(actsHighlight)
	}
	const handleDateHighlightChanged = (date: DateNumber) => {
		plannerSo.setDateHighlight(date)
	}
	const handleClick = (e: EventMouseGantt) => {
		taskSo.setSelected([])
		// selezione sulla TIMELINE
		if (e.row == "timeline") {
			const range = getRangeFromEvent(e, dateRangeSel)
			querySo.setSearchUrl([URL_PAR.DATE_SEL, dateToUrl(range)])
			// se ho selezionato un CYCLE allora lo setto nell'URL
		} else {
			const cycles = e.activity?.tags?.[TAGS.CYCLES]
			querySo.setSearchUrl([URL_PAR.CYCLE_SEL, cycles?.[0]])
		}
	}



	// RENDER
	//const rangeDuration = useMemo(() => daysDuration(dateRange), [dateRange])
	if (solutionLabSo.state.selected == null) return <PlaceholderCmp
		title="No solution selected"
		subtitle="Click on SOLUTIONS and choose one"
		iconType={ICON_TYPE.VOID}
	/>

	return <>
		<Graph
			readOnly={true}
			groups={plannerSa.groups}

			activitySelect={plannerSa.select}
			activityHighlight={plannerSa.highlight}
			dateHighlight={plannerSa.dateHighlight}

			dateStartView={plannerSa.dateStart}
			columnsEndView={plannerSa.columnsEnd}
			dateCurrent={plannerSa.dateCurrent}
			dateRange={dateRangeSel}
			genericDateRange={plannerSa.genericDateRange}

			style={style}

			onChangeGroups={handleChangeGroups}
			onHorizontalScroll={handleHorizontalScroll}
			onActivityHighlightChanged={handleActivityHighlightChanged}
			onDateHighlightChanged={handleDateHighlightChanged}
			onClick={handleClick}
		/>
		<TooltipCycle
			activity={tipActivity}
			pos={tipPos}
		/>
	</>
}

export default PlannerLab;